import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../ui/Icon';
import useWindowSize from '../hooks/useWindowSize';
import { T } from '../constants/mockData';
import { applyAccentColor, applyFullTheme, DEFAULT_ACCENT, THEME_PRESETS } from '../constants/theme';
import { ticketsAPI } from '../services/api';
import DashboardPage from '../pages/dashboard/DashboardPage';
import ExecutiveDashboard from '../pages/dashboard/ExecutiveDashboard';
import ListingsPage from '../pages/listings/ListingsPage';
import AdminPage from '../pages/admin/AdminPage';
import WorkflowPage from '../pages/workflow/WorkflowPage';
import AIChatbot, { TICKET_CHAT_CONFIG } from '../pages/chat/AIChatbot';
import FloatingAssistantsHub from '../pages/chat/FloatingAssistantsHub';
import ToastContainer from '../ui/ToastContainer';
import ReportsPage from '../pages/reports/ReportsPage';
import { formatInTz } from '../utils/timezone';
import Topbar from './Topbar';
import ProfileSettingsModal from '../modals/ProfileSettingsModal';
import OutOfOfficeModal from '../modals/OutOfOfficeModal';
import AppearanceModal from '../modals/AppearanceModal';

// ── Notification sound (Web Audio API — no external files) ───────────────────
function playNotifSound(type = 'notification') {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const play = (freq, t, dur, vol = 0.22) => {
      const osc = ctx.createOscillator();
      const g   = ctx.createGain();
      osc.connect(g); g.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, t);
      g.gain.setValueAtTime(vol, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + dur);
      osc.start(t); osc.stop(t + dur);
    };
    const now = ctx.currentTime;
    if (type === 'new_ticket') {
      play(880,  now,        0.25);
      play(1100, now + 0.2,  0.35);
    } else {
      play(660, now, 0.3);
    }
    setTimeout(() => { try { ctx.close(); } catch {} }, 1200);
  } catch { /* ignore */ }
}

const ADMIN_SUB_ITEMS = ["Agent Groups", "Agent Users", "Requester Groups", "Requesters", "Workflow Automator", "Master Data", "SLA Policies", "Priority Keywords", "Knowledge Base", "Reset", "M365 Automation", "M365 User", "M365 Group", "M365 Users", "M365 Groups", "Pause Requests", "Attendance", "Attendance Settings", "Email Notifications", "Timezone", "Workspace Settings", "Audit Logs"];

// Full menu for all roles (add role-based restrictions later)
const FULL_SIDEBAR_ITEMS = [
  { label: "Executive Overview", icon: "chart" },
  { label: "Dashboard",          icon: "grid" },
  { label: "Tickets",            icon: "list" },
  { label: "Reports",            icon: "bar" },
  { label: "AI Assistant",       icon: "chat" },
  { label: "PowerDash",          icon: "externalLink", external: "https://powerdash.sysnet.com.sg/" },
  { label: "Admin",              icon: "settings", sub: true },
];

function PlaceholderPage({ name }) {
  return (
    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 12, color: T.muted }}>
      <div style={{ width: 56, height: 56, borderRadius: 14, background: T.g50, border: `1.5px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Icon name="info" size={24} color={T.active} />
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, color: "#1A202C" }}>{name}</div>
      <div style={{ fontSize: 12, color: T.muted }}>This section is coming soon.</div>
    </div>
  );
}

function Sidebar({ activeNav, navigate, onLogout, collapsed, setCollapsed }) {
  const [hovered, setHovered] = useState(null);
  const location = useLocation();
  const subItemSlugs = { "Groups": "groups", "Users": "users", "Agent Groups": "agent-groups", "Agent Users": "agent-users", "Agents": "agents", "Requester Groups": "requester-groups", "Requesters": "requesters", "Master Data": "master-data", "SLA Policies": "sla-policies", "Priority Keywords": "priority-keywords", "Knowledge Base": "knowledge-base", "Reset": "reset", "M365 Automation": "m365-automation", "M365 User": "m365-user", "M365 Group": "m365-group", "M365 Users": "m365-users", "M365 Groups": "m365-groups", "Pause Requests": "pause-requests", "Attendance": "attendance", "Attendance Settings": "attendance-settings", "Email Notifications": "email-notifications", "Timezone": "timezone", "Workspace Settings": "workspace-settings", "Workflow Automator": "workflow-automator", "Audit Logs": "audit-logs" };
  const currentAdminSlug = location.pathname.startsWith('/admin/') ? location.pathname.slice('/admin/'.length) : 'groups';

  const btnStyle = (active) => ({
    width: "100%",
    height: 42,
    border: "none",
    borderRadius: 8,
    padding: "0 12px",
    display: "flex",
    alignItems: "center",
    gap: 10,
    cursor: "pointer",
    textAlign: "left",
    fontSize: 13,
    fontWeight: 600,
    background: active ? T.active : "transparent",
    color: "#1A202C",
    transition: "background 0.15s",
  });

  return (
    <div style={{ width: collapsed ? 64 : 220, background: 'linear-gradient(180deg, var(--sidebar) 0%, var(--sidebar-end) 100%)', borderRight: 'none', display: "flex", flexDirection: "column", padding: "16px 0", flexShrink: 0, transition: "width .25s", overflow: "hidden", minHeight: 0 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "flex-start", padding: collapsed ? "0 8px" : "0 14px", marginBottom: 24, gap: collapsed ? 0 : 10, minHeight: 36, overflow: "hidden" }}>
        {!collapsed && (
          <>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: 'linear-gradient(135deg, var(--accent), var(--accent-dark))', display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 16px rgba(0,0,0,0.25)", flexShrink: 0 }}>
              <Icon name="ticket" size={18} color="#fff" />
            </div>
            <span style={{ fontSize: 15, fontWeight: 800, color: "#FFFFFF", flex: 1, whiteSpace: "nowrap", letterSpacing: "-0.02em", overflow: "hidden" }}>PowerDesk</span>
          </>
        )}
        <button
          onClick={() => setCollapsed(c => !c)}
          style={{ width: 26, height: 26, minWidth: 26, border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, background: "rgba(255,255,255,0.08)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
        >
          <Icon name={collapsed ? "chevronR" : "chevronL"} size={12} color="rgba(255,255,255,0.5)" />
        </button>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', padding: "0 10px" }}>
        {FULL_SIDEBAR_ITEMS.map(item => {
          const isActive = activeNav === item.label;
          const isHovered = hovered === item.label;
          return (
            <React.Fragment key={item.label}>
              <button
                style={{
                  ...btnStyle(isActive),
                  background: isActive ? 'rgba(255,255,255,0.15)' : isHovered ? "rgba(255,255,255,0.07)" : "transparent",
                  color: isActive ? "#FFFFFF" : "rgba(255,255,255,0.65)",
                  boxShadow: isActive ? "none" : "none",
                  borderLeft: isActive ? "3px solid var(--accent)" : "3px solid transparent",
                  marginBottom: 4,
                }}
                onClick={() => {
                  if (item.external) {
                    window.open(item.external, '_blank', 'noopener,noreferrer');
                    return;
                  }
                  const routeMap = {
                    'Executive Overview': '/executive',
                    'AI Assistant': '/ai-assistant',
                    'Admin': '/admin/groups',
                  };
                  navigate(routeMap[item.label] || '/' + item.label.toLowerCase());
                }}
                onMouseEnter={() => setHovered(item.label)}
                onMouseLeave={() => setHovered(null)}
              >
                <Icon name={item.icon} size={18} color={isActive ? "#FFFFFF" : isHovered ? "#FFFFFF" : "rgba(255,255,255,0.5)"} />
                {!collapsed && <span style={{ overflow: "hidden" }}>{item.label}</span>}
              </button>

              {item.label === "Admin" && activeNav === "Admin" && !collapsed && (
                <div style={{ display: "flex", flexDirection: "column", gap: 4, paddingLeft: 18, marginBottom: 8 }}>
                  {ADMIN_SUB_ITEMS.map(subItem => {
                    const subActive = subItemSlugs[subItem] === currentAdminSlug;
                    return (
                      <button
                        key={subItem}
                        onClick={() => navigate('/admin/' + subItemSlugs[subItem])}
                        style={{
                          width: "100%",
                          textAlign: "left",
                          padding: "8px 12px",
                          border: "none",
                          borderRadius: 8,
                          background: subActive ? "rgba(255,255,255,0.12)" : "transparent",
                          color: subActive ? "#FFFFFF" : "rgba(255,255,255,0.5)",
                          fontSize: 12,
                          fontWeight: subActive ? 700 : 600,
                          cursor: "pointer",
                        }}
                      >
                        {subItem}
                      </button>
                    );
                  })}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      <div style={{ height: 1, background: "rgba(255,255,255,0.1)", margin: "6px 10px", flexShrink: 0 }} />

      <button
        style={{
          ...btnStyle(false),
          background: hovered === "__logout__" ? "rgba(239,68,68,0.15)" : "transparent",
          color: "rgba(248,113,113,0.85)",
          margin: "0 10px"
        }}
        onClick={onLogout}
        onMouseEnter={() => setHovered("__logout__")}
        onMouseLeave={() => setHovered(null)}
      >
        <Icon name="logout" size={15} color="#f87171" />
        {!collapsed && <span style={{ color: "#f87171" }}>Logout</span>}
      </button>
    </div>
  );
}

export default function Dashboard({ onLogout, currentUser }) {
  const size = useWindowSize();
  const isMobile = size < 768;
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
    else setSidebarOpen(true);
  }, [isMobile]);

  const location = useLocation();
  const navigate = useNavigate();
  const activeNav = (() => {
    const p = location.pathname;
    if (p.startsWith('/tickets'))     return 'Tickets';
    if (p.startsWith('/reports'))     return 'Reports';
    if (p.startsWith('/admin'))       return 'Admin';
    if (p === '/executive')           return 'Executive Overview';
    if (p === '/ai-assistant')        return 'AI Assistant';
    return 'Dashboard';
  })();
  const adminSlugToName = { 'groups': 'Groups', 'users': 'Users', 'agent-groups': 'Agent Groups', 'agent-users': 'Agent Users', 'agents': 'Agents', 'requester-groups': 'Requester Groups', 'requesters': 'Requesters', 'master-data': 'Master Data', 'sla-policies': 'SLA Policies', 'priority-keywords': 'Priority Keywords', 'knowledge-base': 'Knowledge Base', 'reset': 'Reset', 'm365-automation': 'M365 Automation', 'm365-user': 'M365 User', 'm365-group': 'M365 Group', 'm365-users': 'M365 Users', 'm365-groups': 'M365 Groups', 'pause-requests': 'Pause Requests', 'attendance': 'Attendance', 'attendance-settings': 'Attendance Settings', 'email-notifications': 'Email Notifications', 'timezone': 'Timezone', 'workspace-settings': 'Workspace Settings', 'workflow-automator': 'Workflow Automator', 'audit-logs': 'Audit Logs' };
  const adminSubSlug = location.pathname.startsWith('/admin/') ? location.pathname.slice('/admin/'.length) : 'groups';
  const adminSubNav = adminSlugToName[adminSubSlug] || 'Groups';
  const [tickets, setTickets] = useState([]);
  const [listings, setListings] = useState([]);
  const [pendingChanges, setPendingChanges] = useState({ new: 0, updated: 0 });
  const knownIdsRef = useRef(new Set());
  const lastRefreshedAtRef = useRef(null);
  const initialLoadDoneRef = useRef(false);
  const lastPendingTotalRef = useRef(0);

  const ticketKey = (t) => {
    const id = t?.id ?? t?._id;
    return id != null ? String(id) : '';
  };

  // Map API ticket to listings-page format
  function mapToListing(t) {
    const fmt = (iso) => iso ? formatInTz(iso, { day: '2-digit', month: 'short', year: 'numeric' }) : '';
    // ticketsAPI.list() already runs mapTicket(), so fields are pre-mapped:
    //   t.assignedTo  = agent name  (from assigned_to_name)
    //   t._assigned_to_id = agent id (from assigned_to)
    //   t.dept        = department
    //   t.group       = group name
    const agentName = t.assignedTo || t.assigned_to_name || '';
    const agentId   = t._assigned_to_id || t.assigned_to || null;
    return {
      _id: t.id,
      id: t.ticket_number || `#${t.id}`,
      name: t.subject || '',
      description: t.description || '',
      requestor: t.requester || t.requester_name || t.requester_email || '',
      email: t.email || t.requester_email || '',
      cc: t.requester_cc || t.cc || '',
      dept: t.dept || t.department || '',
      orgUnit: t.org_unit || '',
      headOfDept: t.head_of_department || '',
      category: t.category || '',
      subCategory: t.sub_category || t.subcategory || '',
      additionalSubCategory: t.additional_sub_category || '',
      status: t.status || 'Open',
      priority: t.priority || 'Low',
      type: t.type || 'Incident',
      source: t.source || 'Email',
      group: t.group || t.assigned_group_name || '',
      _assigned_group_id: t._assigned_group_id || null,
      _assigned_to_id: agentId,
      assignedTo: agentName,
      assignedAgent: agentId ? { id: agentId, name: agentName } : (agentName ? { id: null, name: agentName } : null),
      actionTaker: agentName,
      actionTaker1: t.action_taker_1_name || '',
      actionTaker1Remarks: t.action_taker_1_remarks || '',
      actionTaker1Timestamp: t.action_taker_1_at ? formatInTz(t.action_taker_1_at, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '',
      actionTaker2: t.action_taker_2_name || '',
      actionTaker2Remarks: t.action_taker_2_remarks || '',
      actionTaker2Timestamp: t.action_taker_2_at ? formatInTz(t.action_taker_2_at, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '',
      actionTaker3: t.action_taker_3_name || '',
      actionTaker3Remarks: t.action_taker_3_remarks || '',
      actionTaker3Timestamp: t.action_taker_3_at ? formatInTz(t.action_taker_3_at, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '',
      requestRating: t.request_rating || '',
      remarks: t.remarks || '',
      procurementCompletionDate: t.procurement_completion_date || '',
      firstResponseTime: t.first_response_at ? formatInTz(t.first_response_at, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '',
      reasonForBreach: t.reason_for_breach || '',
      endUser: t.end_user || t.requester || t.requester_name || t.requester_email || '',
      vendor: '',
      created: t.created || fmt(t.created_at),
      _created_at: t._created_at || t.created_at || null,
      modified: t.modified || fmt(t.updated_at),
      slaStatus: t.sla_status || t.slaStatus || null,
      resolutionDueAt: t.resolution_due_at || t.resolutionDueAt || null,
      firstResponseDueAt: t.first_response_due_at || t.firstResponseDueAt || null,
      due_by: t.due_by || null,
      latestReplyBody: t.latest_reply_body || '',
      latestReplySender: t.latest_reply_sender || '',
      latestReplyAt: t.latest_reply_at || null,
      first_response_at: t.first_response_at || null,
      latestConversationMode: t.latestConversationMode || null,
      latestConversationFromEmail: t.latestConversationFromEmail || false,
      latestConversationSenderId: t.latestConversationSenderId ?? null,
      latestConversationSenderEmail: t.latestConversationSenderEmail || '',
      timer_status: t.timer_status || 'stopped',
      timer_elapsed_ms: Number(t.timer_elapsed_ms) || 0,
      timer_started_at: t.timer_started_at || null,
    };
  }

  // ── Initial load ──────────────────────────────────────────────────────────
  const loadTickets = useCallback(() => {
    return ticketsAPI.list({ limit: 200 })
      .then(res => {
        const raw = res.tickets || [];
        setTickets(raw);
        setListings(raw.map(mapToListing));
        knownIdsRef.current = new Set(raw.map(ticketKey).filter(Boolean));
        lastRefreshedAtRef.current = new Date().toISOString();
        initialLoadDoneRef.current = true;
        lastPendingTotalRef.current = 0;
        setPendingChanges({ new: 0, updated: 0 });
      })
      .catch(err => console.error('Failed to load tickets:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadTickets(); }, [loadTickets]);

  // ── Background polling — tickets created/updated since last refresh ──
  useEffect(() => {
    const poll = () => {
      if (!initialLoadDoneRef.current || !lastRefreshedAtRef.current) return;
      ticketsAPI.list({
        since: lastRefreshedAtRef.current,
        limit: 500,
        sortKey: 'updated_at',
        sortDir: 'desc',
      })
        .then(res => {
          const changed = res.tickets || [];
          let newCount = 0;
          let updatedCount = 0;
          changed.forEach(t => {
            const key = ticketKey(t);
            if (!key) return;
            if (!knownIdsRef.current.has(key)) newCount += 1;
            else updatedCount += 1;
          });
          setPendingChanges({ new: newCount, updated: updatedCount });
          const total = newCount + updatedCount;
          if (total > lastPendingTotalRef.current) {
            playNotifSound(newCount > 0 ? 'new_ticket' : 'update');
          }
          lastPendingTotalRef.current = total;
        })
        .catch(() => {});
    };
    const boot = setTimeout(poll, 8000);
    const id = setInterval(poll, 30000);
    return () => { clearTimeout(boot); clearInterval(id); };
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // Called from ListingsPage banner
  const handleRefreshTickets = useCallback(() => { loadTickets(); }, [loadTickets]);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showOutOfOfficeModal, setShowOutOfOfficeModal] = useState(false);
  const [showAppearanceModal, setShowAppearanceModal] = useState(false);

  const [userProfile, setUserProfile] = useState(() => ({
    name: currentUser?.name || '',
    email: currentUser?.email || '',
    avatar: '',
  }));

  const [outOfOffice, setOutOfOffice] = useState({
    enabled: false,
    startDate: "",
    endDate: "",
    message: ""
  });

  const [theme, setTheme] = useState("light");
  const [accentColor, setAccentColor] = useState(() => localStorage.getItem('accentColor') || DEFAULT_ACCENT);
  const [themeName,   setThemeName]   = useState(() => localStorage.getItem('themeName')   || 'default');

  // Apply saved full theme on mount
  useEffect(() => {
    const saved = localStorage.getItem('themeName') || 'default';
    if (saved !== 'default') {
      applyFullTheme(saved);
    } else {
      applyAccentColor(localStorage.getItem('accentColor') || DEFAULT_ACCENT);
    }
  }, []); // eslint-disable-line

  const topbarTitleMap = { Dashboard: "My Dashboard", Tickets: "Tickets", Reports: "Reports", Admin: adminSubNav, "Executive Overview": "Executive Overview", "AI Assistant": "AI Assistant" };
  const topbarTitle = topbarTitleMap[activeNav] || activeNav;
  const onTicketsPage = false;

  return (
    <div style={{ display: "flex", height: "100vh", background: 'var(--bg)', fontFamily: "'DM Sans', system-ui, sans-serif", position: "relative" }}>
      {/* Sidebar Overlay for Mobile */}
      {isMobile && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", zIndex: 100 }}
        />
      )}

      <div style={{
        position: isMobile ? "fixed" : "relative",
        height: "100%",
        zIndex: 101,
        transform: isMobile && !sidebarOpen ? "translateX(-100%)" : "translateX(0)",
        transition: "transform 0.3s ease, width 0.3s ease",
        display: "flex"
      }}>
        <Sidebar
          activeNav={activeNav}
          navigate={navigate}
          onLogout={onLogout}
          collapsed={!sidebarOpen && !isMobile}
          setCollapsed={setSidebarOpen}
        />
      </div>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Topbar 
          isMobile={isMobile}
          setSidebarOpen={setSidebarOpen}
          topbarTitle={topbarTitle}
          onTicketsPage={onTicketsPage}
          userProfile={userProfile}
          onOpenSettings={() => setShowSettingsModal(true)}
          onOpenOutOfOffice={() => setShowOutOfOfficeModal(true)}
          onOpenAppearance={() => setShowAppearanceModal(true)}
          onLogout={onLogout}
        />

        <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column", position: "relative" }}>
          {activeNav === "Executive Overview" && <ExecutiveDashboard currentUser={currentUser} />}

          {/* All roles with access */}
          {activeNav === "Dashboard"    && <DashboardPage tickets={tickets} />}
          {activeNav === "Tickets"      && (
            <ListingsPage
              listings={listings}
              setListings={setListings}
              pendingChanges={pendingChanges}
              onRefreshTickets={handleRefreshTickets}
            />
          )}
          {activeNav === "Reports"      && <ReportsPage />}
          {activeNav === "AI Assistant" && (
            <AIChatbot
              displayName={userProfile.name || ''}
              prefilledEmail={userProfile.email || ''}
              {...TICKET_CHAT_CONFIG}
            />
          )}
          {activeNav === "Admin" && adminSubNav === "Workflow Automator" && <WorkflowPage />}
          {activeNav === "Admin" && adminSubNav !== "Workflow Automator" && <AdminPage activeTab={adminSubNav} setActiveTab={() => {}} />}
        </div>
      </div>

      <FloatingAssistantsHub
        displayName={userProfile.name || ''}
        prefilledEmail={userProfile.email || ''}
        showAi={activeNav !== 'AI Assistant'}
        chatbotProps={TICKET_CHAT_CONFIG}
        voiceProps={{ useBackendAgent: true, useFaq: false }}
      />
      <ToastContainer />

      <ProfileSettingsModal 
        isOpen={showSettingsModal} 
        onClose={() => setShowSettingsModal(false)} 
        userProfile={userProfile} 
        onSave={setUserProfile} 
      />
      
      <OutOfOfficeModal 
        isOpen={showOutOfOfficeModal} 
        onClose={() => setShowOutOfOfficeModal(false)} 
        outOfOffice={outOfOffice} 
        onSave={setOutOfOffice} 
      />
      
      <AppearanceModal 
        isOpen={showAppearanceModal} 
        onClose={() => setShowAppearanceModal(false)} 
        theme={theme}
        accentColor={accentColor}
        themeName={themeName}
        onSave={(newTheme, newAccent, newThemeName) => {
          setTheme(newTheme);
          if (newThemeName && newThemeName !== 'default') {
            setThemeName(newThemeName);
            localStorage.setItem('themeName', newThemeName);
            localStorage.removeItem('accentColor');
            applyFullTheme(newThemeName);
          } else if (newAccent) {
            setAccentColor(newAccent);
            setThemeName('default');
            localStorage.setItem('accentColor', newAccent);
            localStorage.setItem('themeName', 'default');
            applyFullTheme('default');
            applyAccentColor(newAccent);
          }
        }}
      />
    </div>
  );
}
